// A note box where "@" tags someone on staff (Stephanie 2026-10-21: "how do we
// tag a rep in a note" — anyone on staff; they get the bell + an email when
// the note is saved). Type @ and a few letters, pick a name (click, or ↑ ↓
// and Enter / Tab); "@Full Name" goes into the note. onChange gives the text
// and the ids of everyone still tagged in it.
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { insertMention, matchPeople, mentionQuery, mentionedIds, splitMentions, type Person } from "@/lib/mentions";

export function useMentionablePeople() {
  return useQuery({
    queryKey: ["staff", "mentionable"],
    queryFn: () => api<{ people: Person[] }>("/staff/mentionable"),
    staleTime: 5 * 60 * 1000,
  });
}

export interface MentionTextareaHandle {
  focus: () => void;
}

export const MentionTextarea = forwardRef<
  MentionTextareaHandle,
  {
    value: string;
    onChange: (text: string, mentionIds: string[]) => void;
    placeholder?: string;
    rows?: number;
    maxLength?: number;
    className?: string;
    disabled?: boolean;
    autoFocus?: boolean;
    "aria-label"?: string;
    onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    onBlur?: () => void;
    id?: string;
  }
>(function MentionTextarea(props, ref) {
  const { value, onChange, className, onKeyDown, onBlur, ...rest } = props;
  const box = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => ({ focus: () => box.current?.focus() }));
  const peopleQ = useMentionablePeople();
  const people = peopleQ.data?.people ?? [];
  const [picked, setPicked] = useState<Person[]>([]);
  const [caret, setCaret] = useState(0);
  const [active, setActive] = useState(0);
  const [closed, setClosed] = useState(false);

  const at = useMemo(() => mentionQuery(value, caret), [value, caret]);
  const matches = useMemo(() => (at ? matchPeople(people, at.query) : []), [at, people]);
  const open = !closed && !!at && matches.length > 0;

  const emit = (text: string, list: Person[]) => onChange(text, mentionedIds(text, list));

  const choose = (p: Person) => {
    if (!at) return;
    const next = insertMention(value, at, p);
    const list = picked.some((x) => x.id === p.id) ? picked : [...picked, p];
    setPicked(list);
    emit(next.text, list);
    setActive(0);
    requestAnimationFrame(() => {
      box.current?.focus();
      box.current?.setSelectionRange(next.caret, next.caret);
      setCaret(next.caret);
    });
  };

  return (
    <div className="relative">
      <textarea
        ref={box}
        {...rest}
        value={value}
        className={className}
        onChange={(e) => {
          setClosed(false);
          setActive(0);
          setCaret(e.target.selectionStart ?? e.target.value.length);
          emit(e.target.value, picked);
        }}
        onSelect={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
        onBlur={() => {
          setTimeout(() => setClosed(true), 150);
          onBlur?.();
        }}
        onFocus={() => setClosed(false)}
        onKeyDown={(e) => {
          if (open) {
            if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (i + 1) % matches.length); return; }
            if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (i - 1 + matches.length) % matches.length); return; }
            if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); choose(matches[active] ?? matches[0]!); return; }
            if (e.key === "Escape") { e.preventDefault(); setClosed(true); return; }
          }
          onKeyDown?.(e);
        }}
        aria-autocomplete="list"
        aria-expanded={open}
      />
      {open ? (
        <ul role="listbox" aria-label="Tag someone" className="absolute left-0 z-50 mt-1 max-h-60 w-64 overflow-auto rounded-md border border-rule bg-white py-1 text-sm shadow-lg">
          {matches.map((p, i) => (
            <li key={p.id} role="option" aria-selected={i === active}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); choose(p); }}
                className={cn("block w-full px-3 py-1.5 text-left text-ink", i === active ? "bg-canvas font-semibold" : "hover:bg-canvas")}
              >
                {p.name}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {!rest.disabled && picked.some((p) => value.includes(`@${p.name}`)) ? (
        <p className="mt-1 text-xs text-muted">
          Tagged: {picked.filter((p) => value.includes(`@${p.name}`)).map((p) => p.name).join(", ")} — they'll get an email and a Flow alert.
        </p>
      ) : null}
    </div>
  );
});

/** A saved note with "@Name" shown in bold. */
export function NoteText({ text, className }: { text: string; className?: string }) {
  const peopleQ = useMentionablePeople();
  const names = (peopleQ.data?.people ?? []).map((p) => p.name);
  return <span className={cn("whitespace-pre-line", className)}>{splitMentions(text, names).map((p, i) => (p.mention ? <strong key={i} className="font-semibold text-ink">{p.text}</strong> : <span key={i}>{p.text}</span>))}</span>;
}
